import React, { Fragment } from 'react';
import EnrichmentListViewerComponent from '../components/enrichment/EnrichmentListViewerComponent';
import { OverlayPanel } from 'primereact/overlaypanel';
import CollectionNames from '../constant/CollectionNames';
import { FormicaProps, withFormica } from '../helpers/withFormica';
import { RootState } from '../redux/store';
import { changePageFilter, changePagePath } from '../redux/actions/PageActions';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import { EnrichmentBase } from '../domain/enrichment/EnrichmentDomain';
import ComponentName from '../constant/ComponentName';
import EnrichmentClient from '../client/enrichment/EnrichmentClient';
import GatewayContainer from '../config/GatewayContainer';
import GatewayNames from '../constant/GatewayNames';
import VariableType from '../constant/VariableType';
import ServiceContainer from '../config/ServiceContainer';
import { ServiceNames } from '../constant/ServiceNames';
import RoleHelper from '../RoleHelper';
import { EnrichmentSchema } from 'domain/enrichment/EnrichmentSchemaDomains';
import { ComponentState, setSelectedItems } from '../redux/actions/ComponentActions';
import CustomDismissible from '../components/custom/CustomDismissible';
import StandardContainerComponent, { StandardSubscriptionComponentArgs } from '../helpers/StandardContainerComponent';
import EnrichmentService from '../service/EnrichmentService';
import LanguageUtil from '../util/LanguageUtil';
import { BulkActionDependencyError } from 'redux/reducers/BulkActionReducer';

class EnrichmentContainer extends StandardContainerComponent<Props, State, EnrichmentBase, EnrichmentSchema> {
  private enrichmentClient: EnrichmentClient = GatewayContainer.getInstance().get(GatewayNames.ENRICHMENT_CLIENT);

  private createOverlayRef: OverlayPanel | null = null;

  private readonly enrichmentService: EnrichmentService = ServiceContainer.getInstance().get(ServiceNames.ENRICHMENT);

  constructor(props: Props) {
    const args: StandardSubscriptionComponentArgs = {
      component: ComponentName.ENRICHMENT,
      withSchema: true,
      itemCollection: CollectionNames.ENRICHMENTS,
      label: LanguageUtil.get({ id: 'common.label.enrichment', defaultMessage: 'Enrichment' }),
    };
    super(props, args);
  }

  public render() {
    return (
      <Fragment>
        {super.render()}
        <OverlayPanel ref={(el) => (this.createOverlayRef = el)}>{this.getCreateItems()}</OverlayPanel>
      </Fragment>
    );
  }

  protected getListView(): React.ReactNode {
    const { items, itemBuilderView, permissions } = this.state;
    return (
      <EnrichmentListViewerComponent
        variables={items}
        currentModel={itemBuilderView?.getLastCurrent()}
        openVariable={this.openComponent}
        views={itemBuilderView?.getListViews()}
        onSortChange={this.onSortChange}
        permissions={permissions}
      />
    );
  }

  private getCreateItems() {
    return (
      <CustomDismissible id={`item-tools-${this.id}`} onDismiss={() => this.createOverlayRef?.hide()}>
        <div className={'flex flex-col gap-y-2 text-xs px-2 py-2 items-start justify-center '}>
          {RoleHelper.canEdit('EnrichmentVelocity') && (
            <p
              onClick={() => this.navigateToCreate('velocity')}
              data-testid={`${ComponentName.ENRICHMENT}_create_velocity`}
              className={'hover:bg-primary hover:text-white rounded px-1 py-1 indent-1 hover:cursor-pointer w-full'}
            >
              {LanguageUtil.get({ id: `pages.enrichment.types.velocity`, defaultMessage: 'Velocity' })}
            </p>
          )}
          {RoleHelper.canEdit('EnrichmentList') && (
            <p
              onClick={() => this.navigateToCreate('list')}
              data-testid={`${ComponentName.ENRICHMENT}_create_list`}
              className={'hover:bg-primary hover:text-white rounded px-1 py-1 indent-1 hover:cursor-pointer w-full'}
            >
              {LanguageUtil.get({ id: `pages.enrichment.types.list`, defaultMessage: 'List' })}
            </p>
          )}
          {RoleHelper.canEdit('EnrichmentApi') && (
            <p
              onClick={() => this.navigateToCreate('api')}
              data-testid={`${ComponentName.ENRICHMENT}_create_api`}
              className={'hover:bg-primary hover:text-white rounded px-1 py-1 indent-1 hover:cursor-pointer w-full'}
            >
              {LanguageUtil.get({ id: `pages.enrichment.types.api`, defaultMessage: 'API' })}
            </p>
          )}
          {RoleHelper.canEdit('EnrichmentQuery') && (
            <p
              onClick={() => this.navigateToCreate('query')}
              data-testid={`${ComponentName.ENRICHMENT}_create_query`}
              className={'hover:bg-primary hover:text-white rounded px-1 py-1 indent-1 hover:cursor-pointer w-full'}
            >
              {LanguageUtil.get({ id: `pages.enrichment.types.query`, defaultMessage: 'Query' })}
            </p>
          )}
          {RoleHelper.canEdit('EnrichmentBasic') && (
            <p
              onClick={() => this.navigateToCreate('basic')}
              data-testid={`${ComponentName.ENRICHMENT}_create_basic`}
              className={'hover:bg-primary hover:text-white rounded px-1 py-1 indent-1 hover:cursor-pointer w-full'}
            >
              {LanguageUtil.get({ id: `pages.enrichment.types.basic`, defaultMessage: 'Basic' })}
            </p>
          )}
        </div>
      </CustomDismissible>
    );
  }

  private navigateToCreate = (path: string) => {
    const {
      router: { navigate },
    } = this.props;
    navigate(`${path}/create`);
  };

  private getRoute(enrichmentType: VariableType): string | undefined {
    switch (enrichmentType) {
      case VariableType.QUERY:
        return 'query';
      case VariableType.BASIC:
        return 'basic';
      case VariableType.LIST:
        return 'list';
      case VariableType.API:
        return 'api';
      case VariableType.VELOCITY:
        return 'velocity';
      default:
        return 'velocity';
    }
  }

  protected openComponent = (name: string) => {
    const { items } = this.state;
    const {
      router: { navigate },
    } = this.props;
    const enrichment = items.find((i) => i.name == name);
    if (enrichment == undefined) return;
    const path = this.getRoute(enrichment.type);
    navigate(`${path}/${enrichment.name}`);
  };

  protected prepareCount = async () => {
    const query = this.getCountFilter();
    const count = await this.enrichmentClient.count(query.build());
    this.setState({ totalRecord: count });
  };

  private getEnrichmentsFromModal(): string[] {
    const {
      component: { selectedComponentIds },
    } = this.props;
    return selectedComponentIds.map((enrichment: EnrichmentBase) => enrichment.name);
  }

  protected async deployAll(): Promise<BulkActionDependencyError[]> {
    return await this.enrichmentService.bulkAction(this.getEnrichmentsFromModal(), 'Deploy');
  }

  protected async undeployAll(): Promise<BulkActionDependencyError[]> {
    return await this.enrichmentService.bulkAction(this.getEnrichmentsFromModal(), 'Undeploy');
  }

  protected async removeAll(): Promise<BulkActionDependencyError[]> {
    return await this.enrichmentService.bulkAction(this.getEnrichmentsFromModal(), 'Remove');
  }

  protected openCreatePage = (e: any) => {
    if (!RoleHelper.canEdit('Enrichment')) return;
    this.createOverlayRef?.toggle(e);
  };
}

interface Props extends ReduxProps, FormicaProps {}

class State {}

const mapStateToProps = (state: RootState) => ({
  itemView: state.page.itemView,
  currentCategory: state.page.currentCategory,
  pagePath: state.page.pagePath,
  component: state.component as ComponentState,
  user: state.auth.user,
  attributes: state.auth.attributes,
});

const mapDispatchToProps = { changePagePath, setSelectedItems, changePageFilter };
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default withFormica(connector(EnrichmentContainer));
